<template>
  <v-col :style="disabled ? '' : 'cursor: pointer;'"
         class="task-card pt-1 mb-3 d-flex flex-row rounded overflow-hidden elevation-1"
         cols="12"
         @click="$emit('edit', task)">
    <div :class="[color]" class="task-left-border ma-n3"></div>
    <div class="ml-6 flex pr-1">
      <div class="font-weight-bold d-flex flex-row align-center justify-space-between">
        <div>
          <template v-if="disabled">
            <div>
              <v-icon :class="[color + '--text']" class="mr-1">{{ priorityIcon }}</v-icon>
              {{ task.status === 'DONE' ? $t('tasks.DONE') : $t(`tasks.priorities.${task.priority}`) }}
            </div>
          </template>
          <v-menu v-if="!disabled" :disabled="$isReadOnlyUser" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-icon :class="[color + '--text']" class="mr-1">{{ priorityIcon }}</v-icon>
                {{ task.status === 'DONE' ? $t('tasks.DONE') : $t(`tasks.priorities.${task.priority}`) }}
                <v-tooltip v-if="voteCount" right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-avatar :color="task.status === 'DONE' ? 'success' : 'primary'"
                              class="caption white--text"
                              rounded
                              size="16"
                              style="margin-top: -4px; margin-left: 6px"
                              v-bind="attrs"
                              v-on="on">
                      {{ voteCount }}
                    </v-avatar>
                  </template>
                  <span>{{ $t('tasks.task_vote_count_tooltip') }}</span>
                </v-tooltip>
              </div>
            </template>
            <template v-if="task.status !== 'DONE'">
              <v-list class="py-0" dense>
                <v-list-item v-for="priority in $taskPriorities"
                             :key="priority.value"
                             :disabled="task.priority === priority.value"
                             @click="handlePriorityChange(priority.value)">
                  <v-list-item-icon class="mr-4">
                    <v-icon :class="[getPriorityColor(priority.value) + '--text']" class="mr-1">
                      {{ getPriorityIcon(priority.value) }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ priority.name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item @click="handleStatusChange('DONE')">
                  <v-list-item-icon class="mr-4">
                    <v-icon class="success--text mr-1">mdi mdi-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('tasks.mark_done') }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
          </v-menu>
        </div>

        <v-btn v-if="!disabled"
               class="mr-n3"
               icon
               small
               text
               @click="() => $emit('edit', task)">
          <v-icon size="16">mdi mdi-pencil</v-icon>
        </v-btn>
      </div>
      <div class="subtitle-1">{{ task.name }}</div>
      <!-- <div class="subtitle-2 mt-3">{{ task.description }}</div> -->
      <div v-if="userInCharge || teamInCharge" class="mt-2">
        <div v-if="userInCharge" class="caption">
           <span :class="[isUserSelf && 'accent--text font-weight-bold' ]"
                 :title="`${$t('tasks.user_in_charge')}: ${userInCharge}`">{{ userInCharge }}</span>
        </div>
      </div>

      <div class="mt-3">
        <v-chip v-if="category" :title="`${$t('tasks.category')}: ${category}`" class="mr-1  mb-1" x-small>
          {{ category }}
        </v-chip>
        <v-chip :title="$t('year')" class="mr-1  mb-1" x-small>
          {{ $formatYear(task.createdAt) }}
        </v-chip>
        <v-chip v-if="teamInCharge" :title="`${$t('tasks.team_in_charge')}: ${teamInCharge}`" class="mr-1 mb-1" x-small>
          {{ teamInCharge }}
        </v-chip>
        <v-chip v-if="task.dueDate" :color="taskDueDateColor" :title="$t('tasks.due_date')" class="mr-1 mb-1" x-small>
          {{ $formatDateNoTime(task.dueDate) }}
        </v-chip>

      </div>
    </div>
  </v-col>
</template>

<script>
import categoryService from "@/service/category.service";
import dayjs from "dayjs";

export default {
  name: "TaskCard",
  props: {
    task: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    voteCount: {
      type: Number,
      default: 0,
    }
  },
  methods: {
    getPriorityIcon(priority) {
      return priority === 'HIGH' ? 'mdi mdi-chevron-up' : priority === 'MEDIUM' ? 'mdi mdi-drag-horizontal-variant' : 'mdi mdi-chevron-down'
    },
    getPriorityColor(priority) {
      return priority === 'HIGH' ? 'error' : priority === 'MEDIUM' ? 'warning' : 'info';
    },
    handleStatusChange(status) {
      this.$emit('change-status', status, this.task)
    },
    handlePriorityChange(priority) {
      this.$emit('change-priority', priority, this.task)
    },
  },
  computed: {
    isUserSelf() {
      return this.task?.userInCharge && this.task?.userInCharge?.id === this.$userInfo?.id;
    },

    userInCharge() {
      if (!this.task || !this.task.userInCharge) return null;
      let name = [this.task.userInCharge.firstName, this.task.userInCharge.lastName].filter(Boolean).join(' ')
      return name ? name : this.task.userInCharge.email
    },
    teamInCharge() {
      if (!this.task || !this.task.team) return null;
      return this.task.team?.name
    },
    color() {
      if (!this.task) return null;
      if (this.task.status === 'DONE') {
        return 'success';
      }
      return this.getPriorityColor(this.task.priority);
    },
    priorityIcon() {
      if (!this.task) return null;
      if (this.task.status === 'DONE') {
        return 'mdi mdi-check';
      }
      return this.getPriorityIcon(this.task.priority);
    },
    category() {
      if (this.task?.type?.endsWith('_help')) {
        return this.$t('tasks.types.' + this.task.type);
      }
      // get task category as string with parents
      let category = this.task.category;
      return categoryService.getCategoryTreeString(category)
    },
    taskDueDateColor() {
      if (!this.task) return null;
      if (this.task.status === 'DONE') {
        return 'success';
      }
      if (this.task.dueDate) {
        const today = dayjs();
        const dueDate = dayjs(this.task.dueDate);
        if (dueDate < today) {
          return 'error';
        }
        if (dueDate < today.add(7, 'day')) {
          return 'warning';
        }
        return 'info'
      }
      return null;
    }
  },
}
</script>

<style lang="scss" scoped>
.task-left-border {
  width: 6px;
  min-width: 6px;
  flex-shrink: 1;
  height: calc(100% + 24px);
  background-color: #4caf50;
}

.task-card {
  background: #fff;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: #f8f8f8;
  }
}
</style>
